import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// Components
import { PlainDropdown } from "../../../components/dropdown/Dropdown";

const toohlSortingIds = {
  ascendingImp: "ascendingImp", // est
  descendingImp: "descendingImp", // est
  descendingTgImp: "descendingTgImp", // tg
};

function getSortingOptions(tgId) {
  // TOOHL Specific
  if (tgId) {
    return [
      {
        id: toohlSortingIds.descendingTgImp,
        label: (
          <span className="d-flex align-items-center">
            TG Imp <i className="fa fa-arrow-up ml-1 opacity-75" />
          </span>
        ),
      },
    ];
  }
  return [
    {
      id: toohlSortingIds.descendingImp,
      label: (
        <span className="d-flex align-items-center">
          Impressions <i className="fa fa-arrow-up ml-1 opacity-75" />
        </span>
      ),
    },
    {
      id: toohlSortingIds.ascendingImp,
      label: (
        <span className="d-flex align-items-center">
          Impressions <i className="fa fa-arrow-down ml-1 opacity-75" />
        </span>
      ),
    },
  ];
}

const sortButtonDataMap = {
  totalOts: "Total Imp",
  otsLitSpecific: "Tg Imp",
  otsLitSpecificPercentage: "Tg %",
};

function SortingBtn({
  requestRoadStretchSort = () => {},
  requestRoadSegmentsSort = () => {},
}) {
  const [sortingId, setSortingId] = useState("");
  const tgId = useSelector((state) => state.tgSpecificOts.tgId);

  // automatically sorting for imp once stretches fetched
  // automatically sorting for tgImp once tg is selected
  useEffect(() => {
    if (tgId) {
      const descTgImpId = toohlSortingIds.descendingTgImp;
      setSortingId(descTgImpId);
      requestRoadStretchSort(descTgImpId);
      requestRoadSegmentsSort(descTgImpId);
    } else {
      const descImpId = toohlSortingIds.descendingImp;
      setSortingId(descImpId);
      requestRoadStretchSort(descImpId);
      requestRoadSegmentsSort(descImpId);
    }
  }, [tgId]);

  // function
  function handleSort(id) {
    setSortingId(id);
    const label = sortButtonDataMap[id];
    requestRoadStretchSort(id, label);
    requestRoadSegmentsSort(id, label);
  }

  const sortingOptions = getSortingOptions(tgId);

  return (
    <PlainDropdown
      items={sortingOptions}
      onItemSelect={(id) => handleSort(id)}
      className="btn shadow-none p-0 justify-content-center"
      textClassName="px-1"
      selectedId={sortingId}
      placeHolder="Apply Sort"
      dropMenuClassName="dropdown-menu-right"
    />
  );
}

// get Ots
function getOts(value) {
  const { otsAvg, otsLitAvg } = value || {};
  const finalOts = otsLitAvg ? otsLitAvg : otsAvg;
  return finalOts || 0;
}

// get Tg Ots
function getTgOts(value) {
  const { targetOts, targetOtsLit } = value || {};
  const finalTgOts = targetOtsLit ? targetOtsLit : targetOts;
  return finalTgOts || 0;
}

// useSortableData
const useSortableData = (roadsData) => {
  const [sortConfig, setSortConfig] = useState(null);
  const sortedItems = useMemo(() => {
    const sortableItems = [...roadsData];

    // sort config contains id and label
    if (sortConfig !== null) {
      sortableItems.sort((valueOne, valueTwo) => {
        // TOOHL Specific Sorting Options
        // -------------------------------------------------------------------------
        // comparing for Impression "Ascending"
        if (
          sortConfig.id === toohlSortingIds.ascendingImp &&
          getOts(valueOne) < getOts(valueTwo)
        ) {
          return -1;
        }

        if (
          sortConfig.id === toohlSortingIds.ascendingImp &&
          getOts(valueOne) > getOts(valueTwo)
        ) {
          return 1;
        }

        // comparing for Impression "Descending"
        if (
          sortConfig.id === toohlSortingIds.descendingImp &&
          getOts(valueOne) < getOts(valueTwo)
        ) {
          return 1;
        }

        if (
          sortConfig.id === toohlSortingIds.descendingImp &&
          getOts(valueOne) > getOts(valueTwo)
        ) {
          return -1;
        }

        // comparing for TG-Impression "Descending"
        if (
          sortConfig.id === toohlSortingIds.descendingTgImp &&
          getTgOts(valueOne) < getTgOts(valueTwo)
        ) {
          return 1;
        }

        if (
          sortConfig.id === toohlSortingIds.descendingTgImp &&
          getTgOts(valueOne) > getTgOts(valueTwo)
        ) {
          return -1;
        }

        return 0;
      });
    }
    // if the sort config is null it will return unsorted data
    return sortableItems;
  }, [roadsData, sortConfig]);

  const requestSort = (id, label) => {
    setSortConfig({ id, label });
  };

  return [sortedItems, requestSort, sortConfig];
};

export { useSortableData, SortingBtn };
